import React, { useState, useEffect, useRef } from "react";

import PropTypes from "prop-types";
import "./AddStageModal.scss";
import { createPortal } from "react-dom";

/**
 * AddStageModal
 * - Presents UI to add a new stage (inline/modal depending on where you mount it)
 * - Builds payload { stageRequestDTOS: [...] } and calls parentSubmit(payload)
 * - Classname prefix: addstagemodal-classname_*
 */

const DEFAULT_FLAG_OPTIONS = [
  "YTS",
  "PROGRESS",
  "WAITING",
  "ON_HOLD",
  "COMPLETED",
  "ABORTED",
];

const toTitleCase = (s) =>
  String(s || "")
    .toLowerCase()
    .split(/[_\s-]+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ""))
    .join(" ");

const normalizeFlagForServer = (f) =>
  String(f ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");

export default function AddStageModal({
  open,
  onClose,
  projectId,
  stages = [], // may be array of categories containing stageResponseDTOList
  onSuccess,
  parentSubmit, // required: parent handles the API call
  availableFlags = null,
}) {
  const [stageName, setStageName] = useState("");
  const [selectedFlag, setSelectedFlag] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [flags, setFlags] = useState([]);
  const nameInputRef = useRef(null);

  // finalChoice: true = Yes, false = No (default No)
  const [finalChoice, setFinalChoice] = useState(false);
  // 🔒 Lock background scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Flatten categories -> stages so modal always works with stage objects
  const flatStages = (stages || []).flatMap((cat) =>
    Array.isArray(cat.stageResponseDTOList) ? cat.stageResponseDTOList : [cat],
  );
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape" && !submitting) {
        e.preventDefault();
        onClose();
      }

      if (e.key === "Enter" && !submitting) {
        // prevent accidental submits from buttons
        if (document.activeElement?.tagName === "BUTTON") return;
        e.preventDefault();
        handleSubmit();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, submitting, stageName]);
  // 🎯 Auto-focus Status Name input when modal opens
  useEffect(() => {
    if (!open) return;

    // wait for portal + DOM paint
    const id = setTimeout(() => {
      nameInputRef.current?.focus();
    }, 0);

    return () => clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setStageName("");
      setSelectedFlag("");
      setSubmitting(false);
      setFlags([]);
      setFinalChoice(false);
      return;
    }

    // DEBUG: show incoming stages prop (flattened) so you can confirm presence of stagePositionId
    console.log(
      "[AddStageModal] incoming (flat) stages:",
      JSON.stringify(flatStages, null, 2),
    );

    // Build flags list: prefer availableFlags prop, else dedupe from stages, else default list
    let flagsList = [];
    if (Array.isArray(availableFlags) && availableFlags.length > 0) {
      flagsList = availableFlags.map((f) =>
        normalizeFlagForServer(String(f || "")),
      );
    } else {
      // collect unique flags from existing stages in uppercase_underscore form
      const seen = new Map();
      for (const st of flatStages || []) {
        const raw = String(st.stageFlag ?? st.flag ?? "").trim();
        if (!raw) continue;
        const norm = normalizeFlagForServer(raw);
        if (!seen.has(norm)) seen.set(norm, norm);
      }
      flagsList = Array.from(seen.values());
      if (flagsList.length === 0) flagsList = DEFAULT_FLAG_OPTIONS.slice();
    }

    setFlags(flagsList);
    const preferred = flagsList.find((f) => f === "PROGRESS");
    setSelectedFlag(preferred || flagsList[0] || DEFAULT_FLAG_OPTIONS[0]);
    setFinalChoice(false); // default No
    setStageName("");
  }, [open, stages, availableFlags]); // note: stages included so this runs when parent updates

  if (!open) return null;

  const buildPayloadAllStages = () => {
    const normalizeFlagForServerLocal = (f) =>
      String(f ?? "")
        .trim()
        .toUpperCase()
        .replace(/\s+/g, "_");

    // compute max and current final pos from flatStages
    let maxPos = 0;
    let currentFinalPos = null;
    for (const s of flatStages || []) {
      for (const p of s.stagePositionResponseDTOList || []) {
        const pos = Number(p.position ?? p.pos ?? NaN);
        if (Number.isFinite(pos) && pos > maxPos) maxPos = pos;
        if (Boolean(p.final ?? p.finalPosition ?? false)) currentFinalPos = pos;
      }
    }

    // map existing stages (immutable copy)
    const existingRequests = (flatStages || []).map((st) => ({
      ...(st.stageId != null ? { stageId: st.stageId } : {}),
      stageName: st.stageName,
      stageFlag: normalizeFlagForServerLocal(st.stageFlag ?? st.flag ?? ""),
      stagePositionRequestDTOList: (st.stagePositionResponseDTOList || []).map(
        (p) => {
          // Preserve the server field 'stagePositionId' if present (backend uses this name)
          const oldStagePositionId =
            p.stagePositionId ?? p.oldStagePositionId ?? p.id ?? null;

          return {
            ...(oldStagePositionId != null ? { oldStagePositionId } : {}),
            position: Number(p.position ?? p.pos ?? 0),
            initial: Boolean(p.initial ?? false),
            finalPosition: Boolean(p.finalPosition ?? p.final ?? false),
          };
        },
      ),
    }));

    // compute where new stage should go
    let newStagePos;
    if (finalChoice) {
      // user wants new stage to be final -> append and clear old finals
      newStagePos = maxPos + 1;
      for (const sr of existingRequests) {
        for (const pos of sr.stagePositionRequestDTOList || []) {
          pos.finalPosition = false;
        }
      }
    } else {
      // user does NOT want new stage final -> insert before current final (if exists)
      if (currentFinalPos != null) {
        newStagePos = currentFinalPos;
        // increment existing positions >= currentFinalPos
        for (const sr of existingRequests) {
          for (const pos of sr.stagePositionRequestDTOList || []) {
            if (Number(pos.position) >= Number(currentFinalPos)) {
              pos.position = Number(pos.position) + 1;
            }
          }
        }
      } else {
        // no final found, append (fallback)
        newStagePos = maxPos + 1;
      }
    }

    // build new stage position WITHOUT stagePositionId (it's new)
    const newStage = {
      stageName: stageName,
      stageFlag: normalizeFlagForServerLocal(selectedFlag),
      stagePositionRequestDTOList: [
        {
          position: Number(newStagePos),
          initial: false,
          finalPosition: Boolean(finalChoice),
        },
      ],
    };

    const payload = {
      stageRequestDTOS: [...existingRequests, newStage],
    };

    // DEBUG: show final payload BEFORE parentSubmit
    console.log(
      "[AddStageModal] built payload (before parentSubmit):",
      JSON.stringify(payload, null, 2),
    );

    return payload;
  };

  const handleSubmit = async () => {
    if (!stageName) {
      alert("Please enter a stage name.");
      return;
    }
    if (typeof parentSubmit !== "function") {
      alert("parentSubmit handler missing.");
      return;
    }

    const payload = buildPayloadAllStages();
    console.log(
      "[AddStageModal] payload (final):",
      JSON.stringify(payload, null, 2),
    );

    try {
      setSubmitting(true);
      await parentSubmit(payload); // parent handles axios/toasts/refresh
      setSubmitting(false);
      onSuccess && onSuccess(payload);
      onClose();
    } catch (err) {
      console.error("AddStageModal parentSubmit error:", err);
      setSubmitting(false);
      alert(err?.message || "Failed to add stage");
    }
  };

  return createPortal(
    <div
      className="addstagemodal-classname__overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        // close only if clicking the backdrop
        if (e.target === e.currentTarget && !submitting) {
          onClose();
        }
      }}
    >
      <div
        className="addstagemodal-classname__modal"
        role="document"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ===== HEADER ===== */}
        <div className="addstagemodal-classname__header">
          <h4 className="addstagemodal-classname__title">Add New Status</h4>
          <button
            className="addstagemodal-classname__close"
            aria-label="Close"
            onClick={() => !submitting && onClose()}
          >
            ✕
          </button>
        </div>

        {/* ===== BODY ===== */}
        <div className="addstagemodal-classname__body">
          <label className="addstagemodal-classname__field">
            <div className="addstagemodal-classname__field-label">
              Status name
            </div>
            <input
              ref={nameInputRef}
              className="addstagemodal-classname__input"
              placeholder="e.g. QA, Review"
              value={stageName}
              onChange={(e) => setStageName(e.target.value)}
              disabled={submitting}
            />
          </label>

          <div className="addstagemodal-classname__field">
            <div className="addstagemodal-classname__field-label">Flag</div>
            <div className="addstagemodal-classname__flag-pills" role="list">
              {flags.map((f) => {
                const active = String(f) === String(selectedFlag);
                return (
                  <button
                    key={f}
                    type="button"
                    className={`addstagemodal-classname__pill ${
                      active ? "active" : ""
                    }`}
                    onClick={() => setSelectedFlag(f)}
                    disabled={submitting}
                    role="listitem"
                  >
                    {toTitleCase(f)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="addstagemodal-classname__field">
            <div className="addstagemodal-classname__field-label">
              Make this the final stage?
            </div>
            <div className="addstagemodal-classname__final-pills">
              <button
                type="button"
                className={`addstagemodal-classname__final-pill ${
                  !finalChoice ? "active" : ""
                }`}
                onClick={() => setFinalChoice(false)}
                disabled={submitting}
              >
                No
              </button>
              <button
                type="button"
                className={`addstagemodal-classname__final-pill ${
                  finalChoice ? "active" : ""
                }`}
                onClick={() => setFinalChoice(true)}
                disabled={submitting}
              >
                Yes
              </button>
            </div>
            <small className="addstagemodal-classname__hint">Default: No</small>
          </div>
        </div>

        {/* ===== FOOTER ===== */}
        <div className="addstagemodal-classname__footer">
          <button
            className="addstagemodal-classname__btn addstagemodal-classname__btn--cancel"
            onClick={() => !submitting && onClose()}
            disabled={submitting}
          >
            Cancel
          </button>

          <button
            className="addstagemodal-classname__btn addstagemodal-classname__btn--save"
            onClick={handleSubmit}
            disabled={submitting || !stageName}
          >
            {submitting ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

AddStageModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  stages: PropTypes.array,
  onSuccess: PropTypes.func,
  parentSubmit: PropTypes.func.isRequired,
  availableFlags: PropTypes.array,
};
